import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaCheck, FaCrown, FaStar, FaGem, FaArrowRight, FaHeart } from "react-icons/fa";
import "./PremiumMembership.css";

const PremiumMembership = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch(
          "https://tulirmatrimony.com/controlapi/planlist.php"
        );
        const result = await response.json();
        console.log(result);
        setPlans(result.body);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching plans:", error);
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const getPlanIcon = (planName) => {
    switch (planName?.toLowerCase()) {
      case "silver":
        return <FaStar className="tlr-plan-icon-silver" />;
      case "gold":
        return <FaCrown className="tlr-plan-icon-gold" />;
      case "platinum":
        return <FaGem className="tlr-plan-icon-platinum" />;
      case "freeplan":
        return <FaHeart className="tlr-plan-icon-free" />;
      default:
        return <FaCrown className="tlr-plan-icon-default" />;
    }
  };

  const getPlanGradient = (planName) => {
    switch (planName?.toLowerCase()) {
      case "silver":
        return "linear-gradient(135deg, #9e9e9e, #607d8b)";
      case "gold":
        return "linear-gradient(135deg, #ffd700, #ffb347)";
      case "platinum":
        return "linear-gradient(135deg, #e5e4e2, #b0c4de)";
      case "freeplan":
        return "linear-gradient(135deg, #4caf50, #8bc34a)";
      default:
        return "linear-gradient(135deg, #1e3c72, #f20487)";
    }
  };

  const getPlanBorder = (planName) => {
    switch (planName?.toLowerCase()) {
      case "gold":
        return "2px solid #ffd700";
      case "platinum":
        return "2px solid #e5e4e2";
      default:
        return "none";
    }
  };

  const getGridClass = () => {
    const planCount = plans.length;
    if (planCount === 1) return "tlr-single-plan";
    if (planCount === 2) return "tlr-two-plans";
    if (planCount === 3) return "tlr-three-plans";
    return "tlr-four-plans";
  };

  // Function to get feature items based on plan
  const getPlanFeatures = (plan) => {
    const planName = plan.name?.toLowerCase();
    const features = [
      { text: "Create & Manage Profile", included: true },
      { text: "Advanced Search Filters", included: true },
      { text: "View Interested Profiles", included: true },
    ];

    if (planName === "freeplan") {
      features.push({ text: "View 5 Profile Matches", included: true });
      features.push({ text: "Basic Support", included: true });
    } else if (planName === "gold") {
      features.push({ text: "200 Profile matches (12 months)", included: true });
      features.push({ text: "Priority Customer Support", included: true });
    } else if (planName === "silver") {
      features.push({ text: "Unlimited Matches", included: true });
      features.push({ text: "Priority Customer Support", included: true });
    } else if (planName === "platinum") {
      features.push({ text: "Unlimited Matches", included: true });
      features.push({ text: "Priority Customer Support", included: true });
    } else {
      features.push({ text: "5 Profile Matches", included: true });
      features.push({ text: "Priority Customer Support", included: true });
    }

    // Premium features for specific plans
    if (planName === "platinum") {
      features.push({ 
        text: "Personal Matchmaker Assistance", 
        included: true,
        isPremium: true,
        icon: "star"
      });
    } else if (planName === "gold") {
      features.push({ 
        text: "Profile Highlight & Booster", 
        included: true,
        isPremium: true,
        icon: "star"
      });
    }

    return features;
  };

  if (loading) {
    return (
      <section className="tlr-premium-membership">
        <div className="tlr-premium-container">
          <div className="tlr-premium-loader">
            <div className="tlr-premium-spinner"></div>
            <p>Loading premium plans...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!plans || plans.length === 0) {
    return null;
  }

  return (
    <section className="tlr-premium-membership">
      {/* Background Decoration */}
      <div className="tlr-premium-bg-decoration">
        <div className="tlr-premium-circle tlr-circle-1"></div>
        <div className="tlr-premium-circle tlr-circle-2"></div>
        <div className="tlr-premium-circle tlr-circle-3"></div>
      </div>

      <div className="tlr-premium-container">
        {/* Section Header */}
        <div className="tlr-premium-header">
          <div className="tlr-premium-badge">
            <span className="tlr-premium-badge-icon">💎</span>
            <span className="tlr-premium-badge-text">Membership Plans</span>
          </div>
          <h2 className="tlr-premium-title">
            Choose Your <span className="tlr-premium-gradient">Perfect Plan</span>
          </h2>
          <p className="tlr-premium-subtitle">
            Select the membership that best suits your needs and start your journey to find the perfect match
          </p>
        </div>

        {/* Plans Grid - Dynamic based on number of plans */}
        <div className={`tlr-premium-plans-grid ${getGridClass()}`}>
          {plans.map((plan, index) => {
            const isPopular = plan.name?.toLowerCase() === "gold";
            const isFree = plan.name?.toLowerCase() === "freeplan";
            const isSilver = plan.name?.toLowerCase() === "silver";
            const isPlatinum = plan.name?.toLowerCase() === "platinum";
            const isGold = plan.name?.toLowerCase() === "gold";
            
            const features = getPlanFeatures(plan);
            
            return (
              <div 
                key={plan.id} 
                className={`tlr-premium-plan-card ${isPopular ? 'tlr-popular-plan' : ''} ${isFree ? 'tlr-free-plan' : ''}`}
                style={{
                  border: getPlanBorder(plan.name),
                  boxShadow: isPopular ? '0 20px 40px rgba(255, 215, 0, 0.2)' : '0 10px 30px rgba(0, 0, 0, 0.08)'
                }}
              >
                {isPopular && (
                  <div className="tlr-popular-badge-premium">
                    <FaStar />
                    <span>Most Popular</span>
                  </div>
                )}
                
                {isFree && (
                  <div className="tlr-free-badge-premium">
                    <span>Free Trial</span>
                  </div>
                )}
                
                <div className="tlr-plan-header-premium" style={{ background: getPlanGradient(plan.name) }}>
                  <div className="tlr-plan-icon-wrapper">
                    {getPlanIcon(plan.name)}
                  </div>
                  <h3 className="tlr-plan-name">{plan.name}</h3>
                  {!isFree && (
                    <div className="tlr-plan-price">
                      <span className="tlr-currency">₹</span>
                      <span className="tlr-amount">{plan.amount}</span>
                      <span className="tlr-duration">/{plan.validity}</span>
                    </div>
                  )}
                  {isFree && (
                    <div className="tlr-plan-price">
                      <span className="tlr-amount-free">Free</span>
                      <span className="tlr-duration">Forever</span>
                    </div>
                  )}
                </div>
                
                <div className="tlr-plan-body">
                  <div className="tlr-plan-description">
                    <p>{plan.description || "Perfect plan for your matrimony journey"}</p>
                  </div>
                  
                  <div className="tlr-plan-features">
                    {features.map((feature, idx) => (
                      <div 
                        key={idx} 
                        className={`tlr-feature-item ${feature.isPremium ? 'tlr-feature-premium' : ''}`}
                      >
                        {feature.isPremium ? (
                          <FaStar className="tlr-feature-star" />
                        ) : (
                          <FaCheck className="tlr-feature-check" />
                        )}
                        <span>{feature.text}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="tlr-plan-action">
                    <Link 
                      to="/Plans" 
                      className={`tlr-plan-btn ${isPopular ? 'tlr-btn-popular' : ''} ${isFree ? 'tlr-btn-free' : ''}`}
                    >
                      <span>Select Plan</span>
                      <FaArrowRight className="tlr-btn-icon" />
                    </Link>
                  </div>
                </div>
                
                {/* Hover Glow Effect */}
                <div className="tlr-plan-glow"></div>
              </div>
            );
          })}
        </div>
        
        {/* Trust Badges */}
        <div className="tlr-trust-badges">
          <div className="tlr-trust-item">
            <span className="tlr-trust-icon">🔒</span>
            <span>Secure Payments</span>
          </div>
          <div className="tlr-trust-item">
            <span className="tlr-trust-icon">⭐</span>
            <span>10,000+ Happy Couples</span>
          </div>
          <div className="tlr-trust-item">
            <span className="tlr-trust-icon">🤝</span>
            <span>Verified Profiles</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PremiumMembership;